import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Calendar from '../../design-system/calendar/Calendar';
import Modal from '../../design-system/modal/Modal';

import styles from './SessionsManagementModal.module.scss';
import { setAvailableScheduleInterval } from '../../../utils/date-helpers';
import {
  getBookingInfoForTutorForTimespan,
  updateAvailableBookingsForTutorForTimespan,
  setSelectedDate,
  getAlternativeCoachesAvailabilityForMonth
} from '../../../actions/bookable';
import CoachUnavailableModal from './CoachUnavailableModal';
import OutOfLicenceModal from './OutOfLicenceModal';
import { addMonths, isAfter } from 'date-fns';
import DropdownAsync from '../../design-system/dropdown-async/DropdownAsync';
import { getAvailableTimezones } from '../../../actions/time';
import { updateAccount } from '../../../actions/user';
import SessionsUnavailableModal from './SessionsUnavailableModal';
// import CloseButton from '../../design-system/button/close-button/CloseButton';
import { TIMEZONES_MAP } from '../../../utils/time-helpers';
import ErrorMessage from '../../design-system/error-message';
import OnLeavingAlert from './on-leaving-alert/on-leaving-alert';
import { selectBookedDates } from '../../../reducers/sessions';
import Legend from './legend';
import mixpanelHelper from '../../../utils/mixpanel-helper';

const SessionsManagementModal = ({
  availableTimezones,
  calendarSubtitle,
  currentTutor,
  daysAvailableThisMonth,
  detectedTimezone,
  disabledActionButton,
  disabledCalendar,
  enableAlertOnLeaving,
  enableCoachChangeWhenUnavailable,
  errorGettingAvailableSessions,
  futureSchedule,
  getAlternativeCoachesAvailabilityForMonthAction,
  getAvailableTimezonesAction,
  getTutorAvailableScheduleAction,
  handleExecution,
  ignoreIds,
  isAvailableDatetimesEmpty,
  coachHasAvailability,
  isExecuting,
  isLoading,
  isLoadingAvailableSchedule,
  isLoadingAvailableTimezones,
  language,
  lastAvailableTimestamp,
  matchedTutor,
  modalMainColor,
  modalSubtitle,
  modalTitle,
  onCloseModal,
  onDirectTimeSelection,
  productType,
  renderAlert,
  renderAppointmentItem,
  renderAdditionalContent,
  selectedTimestamp,
  selectedTimestamps,
  selectedTutor,
  setSelectedDateAction,
  shouldDetectTimezone,
  student,
  shouldShowLegend,
  testId,
  timesAvailableThisDay,
  timezone,
  updateAccountAction,
  updateAvailableBookingsForTutorForTimespanAction,
  type,
  alternativeAvailabilitiesByMonth,
  onAlternativeTimeSelection
}) => {
  const [selectedTimezone, setSelectedTimezone] = useState(timezone);
  const [isUTC, setIsUTC] = useState(false);
  const [isDumbLoading, setIsDumbLoading] = useState(shouldDetectTimezone ? true : false);
  const [isAlertOnLeavingOpen, setIsAlertOnLeavingOpen] = useState(false);
  const [showAlternativeAvailability, setShowAlternativeAvailability] = useState(false);
  const bookedTimestamps = useSelector((state) => selectBookedDates(state));
  const tutor = currentTutor?.id ? currentTutor : selectedTutor?.id ? selectedTutor : matchedTutor;

  const isTimezoneUTC = (tz) => {
    return tz === TIMEZONES_MAP.get('UTC');
  };

  const autoDetectTimezone = () => {
    setTimeout(() => {
      setSelectedTimezone(detectedTimezone);
      setIsDumbLoading(false);
    }, 3000);
  };

  useEffect(() => {
    mixpanelHelper.openedModal('Sessions Management Modal', { type });
  }, []);

  useEffect(() => {
    shouldDetectTimezone && detectedTimezone && autoDetectTimezone();
  }, [detectedTimezone]);

  useEffect(() => {
    if (timezone) setSelectedTimezone(timezone);
  }, [timezone]);

  useEffect(() => {
    setIsUTC(isTimezoneUTC(selectedTimezone));
  }, [selectedTimezone]);

  const getTutorAvailableScheduleForSpecificMonthspan = (numberOfMonths = 1) => {
    const { from, to } = setAvailableScheduleInterval(new Date(), 0, numberOfMonths);
    getTutorAvailableScheduleAction({
      token: student.token,
      tutorId: tutor?.id,
      productTypeId: productType.productTypeId,
      selectedLanguageId: language.id,
      from,
      to,
      ignoreIds
    });
  };

  useEffect(() => {
    if (tutor?.id && productType?.productTypeId) getTutorAvailableScheduleForSpecificMonthspan(12);
  }, [tutor?.id, productType?.productTypeId]);

  // Auto-select the first available date when available days are loaded
  useEffect(() => {
    if (daysAvailableThisMonth?.length > 0 && !selectedTimestamp && !isLoadingAvailableSchedule) {
      const firstAvailableDate = daysAvailableThisMonth[0];
      setSelectedDateAction(firstAvailableDate);
    }
  }, [daysAvailableThisMonth, selectedTimestamp, isLoadingAvailableSchedule, setSelectedDateAction]);

  // Fetch alternative availability when the calendar's displayed month is known
  useEffect(() => {
    if (!tutor?.id || !productType?.productTypeId) return;

    // Determine which date to use for fetching
    let dateToFetch;
    let source;

    if (daysAvailableThisMonth?.length > 0) {
      // Use the actual displayed month from calendar
      const firstAvailableDay = daysAvailableThisMonth[0];
      dateToFetch = new Date(firstAvailableDay);
      source = 'calendar';
    } else if (!isLoadingAvailableSchedule) {
      // Fallback to system current month while calendar is loading
      dateToFetch = new Date();
      source = 'system';
    } else {
      // Still loading, don't fetch yet
      return;
    }

    const year = dateToFetch.getFullYear();
    const month = dateToFetch.getMonth();
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

    // Check if we already have data for this month (prevent duplicate fetches)
    if (alternativeAvailabilitiesByMonth && alternativeAvailabilitiesByMonth[monthKey]) {
      console.log(`✅ Already have alternative availability for ${monthKey}, skipping fetch`);
      return;
    }

    console.log(`📅 Fetching alternative availability for ${monthKey} (source: ${source})`);

    getAlternativeCoachesAvailabilityForMonthAction({
      token: student.token,
      tutorId: tutor?.id,
      productTypeId: productType.productTypeId,
      selectedLanguageId: language.id,
      date: dateToFetch,
      ignoreIds
    });
  }, [daysAvailableThisMonth?.[0], tutor?.id, productType?.productTypeId, isLoadingAvailableSchedule]); // Trigger when displayed month changes

  const isOneMonthFromLastAvailable = (ts) => {
    const oneMonthFromDate = addMonths(new Date(ts), 1);
    return isAfter(oneMonthFromDate, new Date(lastAvailableTimestamp));
  };

  // Get alternative availability for the current calendar month
  const getCurrentMonthAlternativeAvailability = () => {
    if (!showAlternativeAvailability || !alternativeAvailabilitiesByMonth) return {};

    // Get current displayed month from the first available day in the calendar
    const displayedDate = daysAvailableThisMonth?.length > 0 ? new Date(daysAvailableThisMonth[0]) : new Date();

    const year = displayedDate.getFullYear();
    const month = displayedDate.getMonth();
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

    console.log('🎨 Getting alternative availability for display, monthKey:', monthKey);
    const data = alternativeAvailabilitiesByMonth[monthKey] || {};
    console.log('🎨 Alternative availability data for display:', Object.keys(data).length, 'time slots');

    return data;
  };

  const updateTutorAvailableSchedule = (ts) => {
    if (isOneMonthFromLastAvailable(ts)) {
      updateAvailableBookingsForTutorForTimespanAction({
        timestamp: lastAvailableTimestamp,
        token: student.token,
        tutorId: tutor.id,
        productTypeId: productType.productTypeId,
        selectedLanguageId: language.id
      });
    }
  };

  const handleCloseModal = () => {
    mixpanelHelper.closedModal('Sessions Management Modal', { type });
    if (onCloseModal) {
      if (!enableAlertOnLeaving || !selectedTimestamps?.length) return onCloseModal();
      return setIsAlertOnLeavingOpen(true);
    }
  };

  const onAcceptClosing = () => {
    onCloseModal();
    setIsAlertOnLeavingOpen(false);
  };

  if (!isLoadingAvailableSchedule && errorGettingAvailableSessions) {
    return (
      <SessionsUnavailableModal
        modalTitle={modalTitle}
        modalMainColor={modalMainColor}
        onCloseModal={handleCloseModal}
        testId={testId}
        tutor={tutor}
      />
    );
  }

  if (!coachHasAvailability && !isLoadingAvailableSchedule && isAvailableDatetimesEmpty) {
    return (
      <CoachUnavailableModal
        disabledActionButton={disabledActionButton}
        enableCoachChangeWhenUnavailable={enableCoachChangeWhenUnavailable}
        modalTitle={modalTitle}
        modalMainColor={modalMainColor}
        onCloseModal={handleCloseModal}
        testId={testId}
        tutor={tutor}
      />
    );
  }

  if (coachHasAvailability && !isLoadingAvailableSchedule && isAvailableDatetimesEmpty) {
    return (
      <OutOfLicenceModal
        disabledActionButton={disabledActionButton}
        enableCoachChangeWhenUnavailable={enableCoachChangeWhenUnavailable}
        modalTitle={modalTitle}
        modalMainColor={modalMainColor}
        onCloseModal={handleCloseModal}
        testId={testId}
        tutor={tutor}
      />
    );
  }

  const handleTimezoneSelection = (tz) => {
    setSelectedTimezone(tz);
    updateAccountAction({ ...student, timezone: tz });
  };

  const onFinish = () => {
    handleExecution();
  };

  const getActionButtonText = () => {
    if (modalTitle && modalTitle.toLowerCase().includes('rescheduling')) {
      return 'Reschedule';
    }
    return 'Book';
  };

  return (
    <>
      <Modal
        noPadding
        completionProgress={100}
        modalMainColor={modalMainColor}
        loading={isLoading}
        onClose={handleCloseModal}
        content={
          <div className={styles.modalWrapper} data-testid={testId}>
            <div className={[styles.modalTitle, styles[modalMainColor]].join(' ')}>
              <img src={tutor?.profile_picture || tutor?.photo} alt="" />
              <div className={styles['main-title']}>
                <h2 data-testid={`${testId}-title`}>
                  {modalTitle} with {tutor?.name || tutor?.first_name}
                </h2>
                <p data-testid={`${testId}-subtitle`}>{modalSubtitle}</p>
              </div>
            </div>
            <div className={[styles.modalSection, styles['timezone-wrapper']].join(' ')}>
              <h2>Select your timezone</h2>
              <div className={styles.dropdown}>
                <DropdownAsync
                  defaultValue={selectedTimezone}
                  options={availableTimezones}
                  onDefaultValue={handleTimezoneSelection}
                  onChange={handleTimezoneSelection}
                  getOptions={() => getAvailableTimezonesAction(student?.token)}
                  isLoading={isLoadingAvailableTimezones || isDumbLoading}
                />
                {isUTC && !isLoadingAvailableTimezones && (
                  <div className={styles.errorWrapper}>
                    <ErrorMessage copy="Please select your real timezone before booking your session." />
                  </div>
                )}
              </div>
            </div>
            <div className={[styles.modalSection, styles.withCalendar].join(' ')}>
              <h2>When do you want to meet?</h2>
              <p>{futureSchedule?.length > 1 ? calendarSubtitle : ''}</p>
              {shouldShowLegend ? (
                <Legend showAlternativeAvailability={showAlternativeAvailability} onToggleAlternativeAvailability={setShowAlternativeAvailability} />
              ) : null}
              <div className={[styles.calendarStuff, renderAlert && styles['calendar-with-alerts']].join(' ')}>
                <Calendar
                  actionButtonText={getActionButtonText()}
                  alreadyBooked={bookedTimestamps}
                  daysAvailableThisMonth={daysAvailableThisMonth}
                  disabled={disabledCalendar || isUTC}
                  disabledFinishButton={disabledActionButton || isUTC}
                  futureSchedule={futureSchedule}
                  isFinishing={isExecuting}
                  isLoading={isLoadingAvailableSchedule}
                  onFinish={onFinish}
                  onDirectTimeSelection={onDirectTimeSelection}
                  renderAlert={renderAlert}
                  renderAppointmentItem={renderAppointmentItem}
                  renderAdditionalContent={renderAdditionalContent}
                  selectedTimestamp={selectedTimestamp}
                  selectedTimestamps={selectedTimestamps}
                  timesAvailableThisDay={timesAvailableThisDay}
                  onMonthChangeForward={updateTutorAvailableSchedule}
                  alternativeAvailabilities={getCurrentMonthAlternativeAvailability()}
                  onAlternativeTimeSelection={onAlternativeTimeSelection}
                />
              </div>
              <p id={styles.timezoneMessage}>My timezone: {selectedTimezone}</p>
            </div>
          </div>
        }
      />
      {isAlertOnLeavingOpen && (
        <OnLeavingAlert
          lessonsPreBooked={selectedTimestamps?.length}
          modalMainColor={modalMainColor}
          onAccept={onAcceptClosing}
          onDecline={() => setIsAlertOnLeavingOpen(false)}
        />
      )}
    </>
  );
};

SessionsManagementModal.propTypes = {
  enableCoachChangeWhenUnavailable: PropTypes.bool,
  availableTimezones: PropTypes.array.isRequired,
  cleanPreviousStateAction: PropTypes.func.isRequired,
  calendarSubtitle: PropTypes.string,
  currentTutor: PropTypes.object,
  daysAvailableThisMonth: PropTypes.array.isRequired,
  detectedTimezone: PropTypes.string,
  disabledActionButton: PropTypes.bool,
  disabledCalendar: PropTypes.bool,
  enableAlertOnLeaving: PropTypes.bool,
  errorGettingAvailableSessions: PropTypes.bool,
  futureSchedule: PropTypes.array,
  getAlternativeCoachesAvailabilityForMonthAction: PropTypes.func.isRequired,
  getAvailableTimezonesAction: PropTypes.func.isRequired,
  getTutorAvailableScheduleAction: PropTypes.func.isRequired,
  handleExecution: PropTypes.func.isRequired,
  ignoreIds: PropTypes.array.isRequired,
  isAvailableDatetimesEmpty: PropTypes.bool.isRequired,
  coachHasAvailability: PropTypes.bool.isRequired,
  isExecuting: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  isLoadingAvailableSchedule: PropTypes.bool.isRequired,
  isLoadingAvailableTimezones: PropTypes.bool.isRequired,
  language: PropTypes.object.isRequired,
  lastAvailableTimestamp: PropTypes.string.isRequired,
  matchedTutor: PropTypes.object,
  modalMainColor: PropTypes.string,
  modalSubtitle: PropTypes.string,
  modalTitle: PropTypes.string,
  onCloseModal: PropTypes.func,
  onDirectTimeSelection: PropTypes.func,
  productType: PropTypes.object.isRequired,
  renderAlert: PropTypes.func,
  renderAppointmentItem: PropTypes.func.isRequired,
  renderAdditionalContent: PropTypes.func,
  selectedTimestamp: PropTypes.string.isRequired,
  selectedTimestamps: PropTypes.array,
  selectedTutor: PropTypes.object,
  setSelectedDateAction: PropTypes.func.isRequired,
  shouldDetectTimezone: PropTypes.bool,
  shouldShowLegend: PropTypes.bool,
  student: PropTypes.object.isRequired,
  timesAvailableThisDay: PropTypes.array.isRequired,
  timezone: PropTypes.string.isRequired,
  updateAccountAction: PropTypes.func.isRequired,
  updateAvailableBookingsForTutorForTimespanAction: PropTypes.func.isRequired,
  testId: PropTypes.string,
  type: PropTypes.string,
  alternativeAvailabilitiesByMonth: PropTypes.object,
  totalAlternativeCoaches: PropTypes.number,
  onAlternativeTimeSelection: PropTypes.func
};

SessionsManagementModal.defaultProps = {
  calendarSubtitle: 'Choose a regular day and time for each session to meet with your coach',
  enableCoachChangeWhenUnavailable: true,
  cleanPreviousStateAction: () => {},
  disabledActionButton: false,
  disabledCalendar: false,
  futureSchedule: [],
  ignoreIds: [],
  selectedTimestamp: '',
  shouldDetectTimezone: false,
  shouldShowLegend: true
};

const mapStateToProps = (state) => ({
  availableTimezones: state.time.availableTimezones,
  daysAvailableThisMonth: state.bookable.available.daysAvailableThisMonth,
  detectedTimezone: state.time.detectedTimezone,
  errorGettingAvailableSessions: state.bookable.available.errorGettingAvailableSessions,
  isAvailableDatetimesEmpty: state.bookable.available.isAvailableDatetimesEmpty,
  coachHasAvailability: state.bookable.available.coachHasAvailability,
  isLoadingAvailableSchedule: state.bookable.available.isLoadingBookable,
  isLoadingAvailableTimezones: state.time.isLoadingAvailableTimezones,
  language: state.selectedLanguage,
  lastAvailableTimestamp: state.bookable.available.lastAvailableTimestamp,
  matchedTutor: state.selectedTutor.matchedTutor,
  selectedTutor: state.selectedTutor.selectedTutor,
  student: state.user,
  timesAvailableThisDay: state.bookable.available.timesAvailableThisDay,
  timezone: state.time.timezone,
  alternativeAvailabilities: state.bookable.available.alternativeAvailabilities,
  alternativeAvailabilitiesByMonth: state.bookable.available.alternativeAvailabilitiesByMonth,
  totalAlternativeCoaches: state.bookable.available.totalAlternativeCoaches
});

export default connect(mapStateToProps, {
  getAlternativeCoachesAvailabilityForMonthAction: getAlternativeCoachesAvailabilityForMonth,
  getAvailableTimezonesAction: getAvailableTimezones,
  getTutorAvailableScheduleAction: getBookingInfoForTutorForTimespan,
  setSelectedDateAction: setSelectedDate,
  updateAccountAction: updateAccount,
  updateAvailableBookingsForTutorForTimespanAction: updateAvailableBookingsForTutorForTimespan
})(SessionsManagementModal);
